/**
 * Configuration options for the element attribute observer
 */
export interface ElementAttributeObserverOptions {
  /** Specific attributes to watch. If not provided, watches all attributes */
  attributes?: string[]
  /** If true, stops observing after first match (default: false) */
  once?: boolean
  /** Maximum time to observe in milliseconds (default: no timeout) */
  timeout?: number
}

/**
 * Information about the attribute change
 */
export interface AttributeChangeInfo {
  /** The element that had its attribute changed */
  element: Element
  /** The name of the attribute that changed */
  attributeName: string
  /** The old value of the attribute (null if it didn't exist) */
  oldValue: string | null
  /** The new value of the attribute (null if it was removed) */
  newValue: string | null
  /** The type of change: 'added', 'modified', or 'removed' */
  changeType: 'added' | 'modified' | 'removed'
}

/**
 * Callback function type for attribute changes
 */
export type AttributeChangeCallback = (info: AttributeChangeInfo) => void

/**
 * Observes attribute changes on a specific DOM element
 * @param element - The DOM element to observe for attribute changes
 * @param callback - Function to call when attributes change. Receives AttributeChangeInfo as parameter.
 * @param options - Optional configuration
 * @returns Observer object with disconnect() method to stop observing
 */
export function onElementAttributeChange(
  element: Element,
  callback: AttributeChangeCallback,
  options: ElementAttributeObserverOptions = {},
): () => void {
  const config: Required<Omit<ElementAttributeObserverOptions, 'attributes'>> &
    Pick<ElementAttributeObserverOptions, 'attributes'> = {
    attributes: options.attributes,
    once: options.once ?? false,
    timeout: options.timeout ?? 0,
  }

  let timeoutId: number | null = null
  let found: boolean = false

  // Helper function to determine change type
  function getChangeType(oldValue: string | null, newValue: string | null): 'added' | 'modified' | 'removed' {
    if (oldValue === null && newValue !== null) {
      return 'added'
    }
    if (oldValue !== null && newValue === null) {
      return 'removed'
    }
    return 'modified'
  }

  // Create MutationObserver
  const observer: MutationObserver = new MutationObserver((mutations: MutationRecord[]): void => {
    if (found && config.once) {
      return
    }

    mutations.forEach((mutation: MutationRecord): void => {
      if (mutation.type === 'attributes' && mutation.target === element) {
        const attributeName = mutation.attributeName!
        const oldValue = mutation.oldValue
        const newValue = element.getAttribute(attributeName)

        // Skip if we're watching specific attributes and this isn't one of them
        if (config.attributes && !config.attributes.includes(attributeName)) {
          return
        }

        const changeType = getChangeType(oldValue, newValue)

        const changeInfo: AttributeChangeInfo = {
          element,
          attributeName,
          oldValue,
          newValue,
          changeType,
        }

        found = true
        callback(changeInfo)

        if (config.once) {
          observer.disconnect()
          if (timeoutId !== null) {
            clearTimeout(timeoutId)
          }
          return
        }
      }
    })
  })

  // Start observing the element
  observer.observe(element, {
    attributes: true,
    attributeOldValue: true,
    attributeFilter: config.attributes, // Only watch specific attributes if specified
  })

  // Set timeout if specified
  if (config.timeout > 0) {
    timeoutId = window.setTimeout((): void => {
      observer.disconnect()
    }, config.timeout)
  }

  // Return observer control object
  const cleanup = () => {
    observer.disconnect()
    if (timeoutId !== null) {
      clearTimeout(timeoutId)
      timeoutId = null
    }
  }

  return cleanup
}
